import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import PageLayout from '@/components/layout/PageLayout';
import { SectionHeading } from '@/components/ui/section-heading';
import { Award, Star, Shield, Zap, Heart, BookOpen, Swords, Target, ThumbsUp, Users, MapPin, Clock, Trophy, BarChart3, Wifi } from 'lucide-react';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '@/components/ui/carousel';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const About = () => {
  const tenets = [
    {
      icon: <Heart className="w-10 h-10 text-taekwondo-red" />,
      title: "Courtesy",
      description: "We practice humility, politeness, and respect in our interactions with others, both inside and outside the dojang."
    },
    {
      icon: <Shield className="w-10 h-10 text-taekwondo-red" />,
      title: "Integrity",
      description: "We uphold the highest standards of honesty and moral principles in our teaching and practice of traditional Taekwondo."
    },
    {
      icon: <Zap className="w-10 h-10 text-taekwondo-red" />,
      title: "Perseverance",
      description: "We overcome obstacles through determination and consistent effort, never giving up in the face of challenges."
    },
    {
      icon: <Star className="w-10 h-10 text-taekwondo-red" />,
      title: "Self Control",
      description: "We cultivate discipline of mind and body, maintaining composure and appropriate behavior in all situations."
    },
    {
      icon: <Award className="w-10 h-10 text-taekwondo-red" />,
      title: "Indomitable Spirit",
      description: "We develop an unbreakable spirit that persists through hardship and faces challenges with courage and confidence."
    }
  ];
  
  // Slideshow images for each class
  const lilDragonsImages = [
    "/images/slides/dragons/cover.jpeg",
    "/images/slides/dragons/dragons_1.jpeg",
    "/images/slides/dragons/dragons_2.jpeg",
    "/images/slides/dragons/dragons_3.jpeg",
    "/images/slides/dragons/dragons_4.jpeg",
    "/images/slides/dragons/dragons_5.jpeg",
    "/images/slides/dragons/dragons_6.jpeg",
    "/images/slides/dragons/dragons_7.jpeg",
    "/images/slides/dragons/dragons_8.jpeg",
    "/images/slides/dragons/dragons_9.jpeg",
    // Add more images as needed
  ];
  
  const cadetsImages = [
    "/images/slides/cadets/cover.jpeg",
    "/images/slides/cadets/cadets_1.jpeg",
    "/images/slides/cadets/cadets_2.jpeg",
    "/images/slides/cadets/cadets_3.jpeg",
    "/images/slides/cadets/cadets_4.jpeg",
    "/images/slides/cadets/cadets_5.jpeg",
    "/images/slides/cadets/cadets_6.jpeg",
    "/images/slides/cadets/cadets_7.jpeg",
    "/images/slides/cadets/cadets_8.jpeg",
    "/images/slides/cadets/cadets_weapon_1.jpeg",
    "/images/slides/cadets/cadets_weapon_2.jpeg",
    "/images/slides/cadets/cadets_weapon_3.jpeg",
    // Add more images as needed
  ];
  
  const adultsImages = [
    "/images/slides/adults/t_and_a_cover_1.jpeg",
    "/images/slides/adults/adults_1.jpeg",
    "/images/slides/adults/adults_2.jpeg",
    "/images/slides/adults/adults_3.jpeg",
    "/images/slides/adults/adults_4.jpeg",
    "/images/slides/adults/adults_5.jpeg",
    "/images/slides/adults/adults_6.jpeg",
    "/images/slides/adults/adults_7.jpeg",
    "/images/slides/adults/adults_8.jpeg",
    // Add more images as needed
  ];

  return (
    <PageLayout>
      <Helmet>
        <title>About OMAC-UK | Taekwondo Classes for All Ages in Nottingham</title>
        <meta name="description" content="Learn about OMAC-UK's Taekwondo programs in Nottingham. Classes for Lil Dragons (ages 4-7), Cadets (8-12), and Teens & Adults. Traditional training with Master Juliet Williams." />
        <meta name="keywords" content="Taekwondo classes Nottingham, children's martial arts, Lil Dragons program, Cadets Taekwondo, adult Taekwondo, Poomsae class, weapons training, sparring classes, Taekwondo tenets, Master Juliet Williams, self-defence Nottingham, martial arts for kids, Gedling Taekwondo, OMAC UK, traditional martial arts" />
        <meta property="og:title" content="About OMAC-UK | Taekwondo Classes for All Ages in Nottingham" />
        <meta property="og:description" content="OMAC-UK offers structured Taekwondo programs for all ages in Nottingham, from Lil Dragons (4-7) to adults, with speciality training in traditional patterns, weapons, and sparring." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://omac-uk.co.uk/about" />
        <meta property="og:image" content="/images/juliet/juliet_medal_cropped.jpg" />
        <link rel="canonical" href="https://omac-uk.co.uk/about" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "MartialArtsSchool",
              "name": "OMAC-UK Taekwondo",
              "description": "OMAC-UK Taekwondo academy offers classes for all ages, from Lil Dragons (4-7) to adults, with specialised training in traditional patterns, weapons, and sparring.",
              "url": "https://omac-uk.co.uk/about",
              "image": "https://omac-uk.co.uk/images/juliet/juliet_medal_cropped.jpg",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "65 Gedling Road",
                "addressLocality": "Gedling",
                "addressRegion": "Nottingham",
                "postalCode": "NG4 3FG",
                "addressCountry": "UK"
              },
              "priceRange": "££",
              "telephone": "01159874857",
              "sameAs": [
                "https://www.facebook.com/omacuk",
                "https://www.instagram.com/omac_uk"
              ],
              "openingHoursSpecification": [
                {
                  "@type": "OpeningHoursSpecification",
                  "dayOfWeek": ["Monday"],
                  "opens": "17:30",
                  "closes": "20:30"
                },
                {
                  "@type": "OpeningHoursSpecification",
                  "dayOfWeek": ["Tuesday"],
                  "opens": "16:30",
                  "closes": "20:00"
                },
                {
                  "@type": "OpeningHoursSpecification",
                  "dayOfWeek": ["Wednesday"],
                  "opens": "17:30",
                  "closes": "20:30"
                },
                {
                  "@type": "OpeningHoursSpecification",
                  "dayOfWeek": ["Thursday"],
                  "opens": "16:30",
                  "closes": "20:00"
                },
                {
                  "@type": "OpeningHoursSpecification",
                  "dayOfWeek": ["Saturday"],
                  "opens": "09:30",
                  "closes": "13:30"
                }
              ],
              "teaches": [
                "Taekwondo",
                "MooGongRyu",
                "Hapkido",
                "Self-Defence",
                "Poomsae",
                "Weapons Training",
                "Sparring"
              ]
            }
          `}
        </script>
      </Helmet>

      {/* Hero Section */}
      <div className="pt-16 pb-12 bg-gray-900 text-white">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold font-display mb-6">About OMAC-UK</h1>
            <p className="text-xl text-gray-300">Dedicated to excellence in Taekwondo training for over three decades</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <img 
                src="/images/juliet/juliet_medal_cropped.jpg"
                alt="OMAC Taekwondo practice" 
                className="rounded-xl shadow-lg"
              />
            </div>
            <div>
              <SectionHeading
                subtitle="Our Mission"
                title="Developing Mind, Body, and Spirit"
                alignment="left"
                gradient={true}
              />
              <p className="text-gray-700 mb-4">
                At OMAC-UK, we're more than just a Taekwondo academy—we're a community. Based in Gedling,
                Nottingham, our school offers age and experience-specific classes, following the World Taekwondo
                curriculum alongside MooGongRyu and Hapkido self-defence techniques.
              </p>
              <p className="text-gray-700 mb-4">
                Whether you're looking to build confidence, improve fitness, learn self-defence, or find a new passion,
                our friendly, family-oriented environment makes training both rewarding and enjoyable.
                Many of our students train alongside parents, siblings, or friends, building skills and
                connections that extend far beyond the dojang.
              </p>
              <p className="text-gray-700">
                We cater to a variety of age groups and experience levels, ensuring that everyone—from complete
                beginners to advanced martial artists—has a place to grow and thrive. Our structured yet fun
                training helps students develop confidence, discipline, and martial arts skills,
                creating a supportive space for personal growth and achievement.
              </p>
            </div>
          </div>

          {/* Core Values */}
          <div className="py-12">
            <SectionHeading
              subtitle="Core Values"
              title="Our Tenets"
              gradient={true}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
              {tenets.map((tenet, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                  <div className="bg-red-50 p-3 rounded-full inline-flex mb-4">
                    {tenet.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{tenet.title}</h3>
                  <p className="text-gray-600">{tenet.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Training Approach */}
          <div className="py-12">
            <SectionHeading
              subtitle="Our Approach"
              title="Training for All Ages"
              gradient={true}
            />
            
            {/* Lil Dragons Section */}
            <div className="mb-16">
              <Card className="overflow-hidden shadow-lg hover:shadow-xl transition-all bg-gradient-to-br from-yellow-50 to-white">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start p-8">
                  <div>
                    <div className="flex items-center mb-4">
                      <div className="bg-yellow-100 text-yellow-600 font-bold rounded-full w-10 h-10 flex items-center justify-center mr-3 flex-shrink-0 shadow-md">1</div>
                      <h3 className="text-xl font-semibold">Lil' Dragons (Ages 4-7)</h3>
                    </div>
                    <h4 className="text-lg font-semibold mb-3 text-taekwondo-red">Building Confidence Through Fun & Discipline</h4>
                    <p className="text-gray-700 mb-4">
                      Our Lil' Dragons programme is designed to lay the foundation for success, both in martial arts and in life. 
                      These engaging and structured classes focus on:
                    </p>
                    <ul className="space-y-2 mb-4">
                      <li className="flex items-start">
                        <span className="text-green-500 font-bold mr-2">✅</span>
                        <div>
                          <span className="font-medium">Life skills</span> – Teaching respect, focus, and teamwork
                        </div>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 font-bold mr-2">✅</span>
                        <div>
                          <span className="font-medium">Safety skills</span> – Learning awareness and basic self-defence
                        </div>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 font-bold mr-2">✅</span>
                        <div>
                          <span className="font-medium">Motor skills</span> – Improving balance, coordination, and agility
                        </div>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 font-bold mr-2">✅</span>
                        <div>
                          <span className="font-medium">Personal development</span> – Boosting confidence through praise and progress
                        </div>
                      </li>
                    </ul>
                    <p className='text-gray-700 mb-3'>
                      Enjoy live-streamed classes in a secure, private group, giving you the opportunity to watch your child train in real-time, join in from home, or rewatch sessions at your convenience to support their Taekwondo journey.
                    </p>
                    <p className="text-gray-700 mb-3">
                      We believe in making learning fun while reinforcing discipline, self-confidence, and non-confrontational self-defence techniques.
                    </p>
                    <div className="flex items-center gap-3">
                      <img 
                        src="/images/icons/lil_dragon_no_text.png" 
                        alt="Lil Dragon" 
                        className="w-12 h-12"
                      />
                      <p className="text-gray-700 italic">
                        This programme provides a strong foundation for students as they prepare to transition into our full Cadets Taekwondo programme at age 7 or 8.
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 lg:mt-0">
                    <Carousel className="w-full">
                      <CarouselContent>
                        {lilDragonsImages.map((image, index) => (
                          <CarouselItem key={index}>
                            <div className="p-1">
                              <img 
                                src={image}
                                alt={`Lil Dragons class ${index + 1}`}
                                className="rounded-xl shadow-md w-full h-[300px] lg:h-[400px] object-cover"
                              />
                            </div>
                          </CarouselItem>
                        ))}
                      </CarouselContent>
                      <div className="flex justify-center mt-4">
                        <CarouselPrevious className="static translate-y-0 mr-2" />
                        <CarouselNext className="static translate-y-0 ml-2" />
                      </div>
                    </Carousel>
                  </div>
                </div>
              </Card>
            </div>
            
            {/* Cadets Section */}
            <div className="mb-16">
              <Card className="overflow-hidden shadow-lg hover:shadow-xl transition-all bg-gradient-to-br from-blue-50 to-white">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start p-8">
                  <div className="order-1 lg:order-2">
                    <div className="flex items-center mb-4">
                      <div className="bg-blue-100 text-taekwondo-blue font-bold rounded-full w-10 h-10 flex items-center justify-center mr-3 flex-shrink-0 shadow-md">2</div>
                      <h3 className="text-xl font-semibold">Cadets (Ages 8-12)</h3>
                    </div>
                    <h4 className="text-lg font-semibold mb-3 text-taekwondo-red">Confidence, Discipline & Self-Defence</h4>
                    <p className="text-gray-700 mb-4">
                      Our Cadets programme is designed to help students develop key life skills while learning Taekwondo techniques 
                      in an encouraging and structured environment. Students build:
                    </p>
                    <ul className="space-y-2 mb-4">
                      <li className="flex items-start">
                        <span className="text-green-500 font-bold mr-2">✅</span>
                        <div>
                          <span className="font-medium">Confidence</span> – Through achievements and goal-setting
                        </div>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 font-bold mr-2">✅</span>
                        <div>
                          <span className="font-medium">Discipline & self-control</span> – Learning respect and perseverance
                        </div>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 font-bold mr-2">✅</span>
                        <div>
                          <span className="font-medium">Self-defence skills</span> – Developing practical techniques
                        </div>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 font-bold mr-2">✅</span>
                        <div>
                          <span className="font-medium">Fitness</span> – Improving agility, strength, and endurance
                        </div>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 font-bold mr-2">✅</span>
                        <div>
                          <span className="font-medium">Friendships</span> – Meeting like-minded peers and training together
                        </div>
                      </li>
                    </ul>
                    
                    <h5 className="font-semibold text-lg mb-3">Cadets Belt Tiers</h5>
                    <div className="mb-3 p-3 bg-blue-50 rounded-lg">
                      <p className="font-medium mb-1">🔵 White Belt to Light Blue Belt – The Foundation Phase</p>
                      <p className="text-gray-700">
                        Beginners are introduced to the core fundamentals of Taekwondo, focusing on technique, control, 
                        and confidence-building. Students develop a solid grounding, allowing them to progress smoothly 
                        as they advance in skill.
                      </p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg">
                      <p className="font-medium mb-1">🟢 Green Belt to Black Belt – Advanced Training & Sparring</p>
                      <p className="text-gray-700">
                        At this level, students refine their techniques and engage in controlled contact sparring, 
                        applying their skills in a real-world setting while continuing to build discipline and self-mastery.
                      </p>
                    </div>
                  </div>
                  <div className="order-2 lg:order-1 mt-4 lg:mt-0">
                    <Carousel className="w-full">
                      <CarouselContent>
                        {cadetsImages.map((image, index) => (
                          <CarouselItem key={index}>
                            <div className="p-1">
                              <img 
                                src={image}
                                alt={`Cadets class ${index + 1}`}
                                className="rounded-xl shadow-md w-full h-[300px] lg:h-[400px] object-cover"
                              />
                            </div>
                          </CarouselItem>
                        ))}
                      </CarouselContent>
                      <div className="flex justify-center mt-4">
                        <CarouselPrevious className="static translate-y-0 mr-2" />
                        <CarouselNext className="static translate-y-0 ml-2" />
                      </div>
                    </Carousel>
                  </div>
                </div>
              </Card>
            </div>
            
            {/* Teens & Adults Section */}
            <div className="mb-16">
              <Card className="overflow-hidden shadow-lg hover:shadow-xl transition-all bg-gradient-to-br from-red-50 to-white">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start p-8">
                  <div>
                    <div className="flex items-center mb-4">
                      <div className="bg-red-100 text-taekwondo-red font-bold rounded-full w-10 h-10 flex items-center justify-center mr-3 flex-shrink-0 shadow-md">3</div>
                      <h3 className="text-xl font-semibold">Teens & Adults (Ages 13+)</h3>
                    </div>
                    <h4 className="text-lg font-semibold mb-3 text-taekwondo-red">Strength, Leadership & Self-Defence</h4>
                    <p className="text-gray-700 mb-4">
                      Designed for teenagers and adults, this programme is ideal for those looking to develop martial arts skills, 
                      enhance fitness, and challenge themselves in a structured and supportive environment.
                    </p>
                    <ul className="space-y-2 mb-4">
                      <li className="flex items-start">
                        <span className="text-green-500 font-bold mr-2">✅</span>
                        <div>
                          <span className="font-medium">Confidence-building</span> – Through personal growth and achievement
                        </div>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 font-bold mr-2">✅</span>
                        <div>
                          <span className="font-medium">Leadership skills</span> – Learning responsibility, respect, and teamwork
                        </div>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 font-bold mr-2">✅</span>
                        <div>
                          <span className="font-medium">Self-defence training</span> – Practical techniques for real-world situations
                        </div>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 font-bold mr-2">✅</span>
                        <div>
                          <span className="font-medium">Goal achievement</span> – Pushing limits, breaking barriers, and excelling
                        </div>
                      </li>
                    </ul>
                    
                    <h5 className="font-semibold text-lg mb-3">Teens & Adults Belt Tiers</h5>
                    <div className="mb-3 p-3 bg-blue-50 rounded-lg">
                      <p className="font-medium mb-1">🔵 White Belt to Light Blue Belt – The Fundamentals</p>
                      <p className="text-gray-700">
                        Beginners focus on core techniques, control, and fitness, building confidence and a strong martial arts foundation.
                      </p>
                    </div>
                    <div className="mb-3 p-3 bg-green-50 rounded-lg">
                      <p className="font-medium mb-1">🟢 Green Belt to Black/White Belt – Intermediate Training & Sparring</p>
                      <p className="text-gray-700">
                        Students continue to develop, with sparring introduced as a key element of training. Control, 
                        discipline, and strategic thinking become vital as they refine their skills.
                      </p>
                    </div>
                    <div className="p-3 bg-gray-800 text-white rounded-lg">
                      <p className="font-medium mb-1">⚫ Black Belt & Black Belt Candidates – Mastery & Excellence</p>
                      <p className="text-gray-200">
                        Our bespoke Black Belt classes provide advanced training for students seeking to refine and perfect 
                        their techniques. Those preparing to test for Black Belt within the next 9 months train alongside 
                        experienced Black Belts, ensuring they are challenged, motivated, and inspired to reach their full potential.
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 lg:mt-0">
                    <Carousel className="w-full">
                      <CarouselContent>
                        {adultsImages.map((image, index) => (
                          <CarouselItem key={index}>
                            <div className="p-1">
                              <img 
                                src={image}
                                alt={`Teens & Adults class ${index + 1}`}
                                className="rounded-xl shadow-md w-full h-[300px] lg:h-[400px] object-cover"
                              />
                            </div>
                          </CarouselItem>
                        ))}
                      </CarouselContent>
                      <div className="flex justify-center mt-4">
                        <CarouselPrevious className="static translate-y-0 mr-2" />
                        <CarouselNext className="static translate-y-0 ml-2" />
                      </div>
                    </Carousel>
                  </div>
                </div>
              </Card>
            </div>
            
            {/* ADDITIONAL SECTIONS */}
            
            {/* Supplemental Classes Section Header */}
            <div className="py-12">
              <SectionHeading
                subtitle="Specialised Training"
                title="Supplemental Classes"
                gradient={true}
              />
              
              {/* Poomsae Class */}
              <div className="mb-16">
                <Card className="overflow-hidden shadow-lg hover:shadow-xl transition-all bg-gradient-to-br from-purple-50 to-white">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start p-8">
                    <div>
                      <div className="flex items-center mb-4">
                        <div className="bg-purple-100 text-purple-600 font-bold rounded-full w-10 h-10 flex items-center justify-center mr-3 flex-shrink-0 shadow-md">4</div>
                        <h3 className="text-xl font-semibold">Poomsae Class</h3>
                      </div>
                      <div className="mb-4 p-2 bg-purple-50 rounded-lg inline-flex">
                        <BookOpen className="w-6 h-6 text-purple-600 mr-2" />
                        <span className="font-medium">Traditional Patterns & Forms</span>
                      </div>
                      <p className="text-gray-700 mb-4">
                        Our Poomsae class is where students refine their technique, focus, and discipline through the traditional patterns (forms) of Taekwondo. 
                        This class is perfect for those looking to master the intricate movements, develop precision, and deepen their understanding of Taekwondo's rich heritage.
                      </p>
                      <p className="text-gray-700 mb-4">
                        Led by Master Juliet, a proud member of the GB National British Taekwondo Poomsae Squad, our students benefit from elite-level instruction straight from the national stage. 
                        With insights from top-tier competition training, we ensure that every student receives the highest standard of coaching—helping them perform at their very best.
                      </p>
                      <p className="text-gray-700">
                        Whether you're a beginner learning your first pattern or an experienced martial artist striving for perfection, 
                        our Poomsae class at OMAC-UK offers a unique opportunity to elevate your Taekwondo journey.
                      </p>
                    </div>
                    <div className="mt-4 lg:mt-0">
                      <img 
                        src="/images/about/poomsae.jpeg"
                        alt="Poomsae training at OMAC-UK" 
                        className="rounded-xl shadow-md w-full h-auto object-cover"
                      />
                    </div>
                  </div>
                </Card>
              </div>
              
              {/* Weapons Class */}
              <div className="mb-16">
                <Card className="overflow-hidden shadow-lg hover:shadow-xl transition-all bg-gradient-to-br from-gray-50 to-white">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start p-8">
                    <div className="order-1 lg:order-2">
                      <div className="flex items-center mb-4">
                        <div className="bg-gray-200 text-gray-700 font-bold rounded-full w-10 h-10 flex items-center justify-center mr-3 flex-shrink-0 shadow-md">5</div>
                        <h3 className="text-xl font-semibold">Weapons Class</h3>
                      </div>
                      <div className="mb-4 p-2 bg-gray-100 rounded-lg inline-flex">
                        <Swords className="w-6 h-6 text-gray-700 mr-2" />
                        <span className="font-medium">Traditional Martial Arts Weaponry</span>
                      </div>
                      <p className="text-gray-700 mb-4">
                        The Weapons Class offers a thrilling opportunity to explore the discipline, control, and precision required for traditional martial arts weaponry. 
                        This bespoke class allows students to develop their skills with a range of weapons, blending technical mastery with dynamic movement.
                      </p>
                      <p className="text-gray-700 mb-4">
                        Our core curriculum focuses on Arnis (sticks) and Bo staff, but we also introduce a variety of other weapons, including swords, nunchaku, sai, 
                        spears, kama, and more, based on student interest. This class is perfect for those looking to enhance coordination, refine technique, 
                        and immerse themselves in the deeper traditions of martial arts.
                      </p>
                      <p className="text-gray-700">
                        Taught in a safe and structured environment, our Weapons Class at OMAC-UK is open to students eager to challenge themselves, 
                        expand their skill set, and experience the artistry of traditional weapon training.
                      </p>
                    </div>
                    <div className="order-2 lg:order-1 mt-4 lg:mt-0">
                      <img 
                        src="/images/about/weapons.jpeg"
                        alt="Martial arts weapons training" 
                        className="rounded-xl shadow-md w-full h-auto object-cover"
                      />
                    </div>
                  </div>
                </Card>
              </div>
              
              {/* Sparring Class */}
              <div className="mb-16">
                <Card className="overflow-hidden shadow-lg hover:shadow-xl transition-all bg-gradient-to-br from-green-50 to-white">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start p-8">
                    <div>
                      <div className="flex items-center mb-4">
                        <div className="bg-green-100 text-green-600 font-bold rounded-full w-10 h-10 flex items-center justify-center mr-3 flex-shrink-0 shadow-md">6</div>
                        <h3 className="text-xl font-semibold">Sparring Class</h3>
                      </div>
                      <div className="mb-4 p-2 bg-green-100 rounded-lg inline-flex">
                        <Target className="w-6 h-6 text-green-600 mr-2" />
                        <span className="font-medium">Applied Combat Techniques</span>
                      </div>
                      <p className="text-gray-700 mb-4">
                        Sparring is where technique meets real-time application. Whether you're looking to challenge yourself, sharpen your reflexes, 
                        or build confidence in controlled combat, our sparring sessions offer a safe, structured, and exhilarating way to put your skills to the test.
                      </p>
                      <p className="text-gray-700 mb-4">
                        Sparring is open to all students who wish to take part, with Green Belts and above required to participate as part of their training progression. 
                        Every session is conducted with full protective gear and pairs students with partners of a similar skill level and size, 
                        ensuring a fair, safe, and developmental experience for all.
                      </p>
                      <p className="text-gray-700">
                        Under the guidance of our expert instructors, students learn to apply their Taekwondo techniques in real-time, 
                        improving speed, strategy, and adaptability — all while reinforcing respect, control, and sportsmanship.
                      </p>
                      <p className="text-gray-700 font-medium italic mt-4">
                        Ready to step onto the mat? Sparring at OMAC-UK is more than just combat — it's about growth, confidence, and embracing the spirit of Taekwondo.
                      </p>
                    </div>
                    <div className="mt-4 lg:mt-0">
                      <img 
                        src="/images/about/sparring_cover.jpeg"
                        alt="Taekwondo sparring session" 
                        className="rounded-xl shadow-md w-full h-auto object-cover"
                      />
                    </div>
                  </div>
                </Card>
              </div>
            </div>
            
            {/* Why Choose OMAC-UK Section */}
            <div className="py-12">
              <SectionHeading
                subtitle="Our Strengths"
                title="Why Choose OMAC-UK"
                gradient={true}
              />
              
              <div className="mb-16">
                <Card className="overflow-hidden shadow-lg hover:shadow-xl transition-all bg-gradient-to-br from-blue-50 to-white">
                  <div className="p-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                      <div>
                        <ul className="space-y-4">
                          <li className="flex items-start">
                            <div className="mt-1 bg-blue-100 p-2 rounded-full mr-3 flex-shrink-0">
                              <Clock className="w-5 h-5 text-taekwondo-blue" />
                            </div>
                            <div>
                              <p className="font-medium">Age & experience-specific classes tailored to individual learning needs</p>
                            </div>
                          </li>
                          <li className="flex items-start">
                            <div className="mt-1 bg-blue-100 p-2 rounded-full mr-3 flex-shrink-0">
                              <Users className="w-5 h-5 text-taekwondo-blue" />
                            </div>
                            <div>
                              <p className="font-medium">Qualified, DBS-checked instructors with assistant support for safety</p>
                            </div>
                          </li>
                          <li className="flex items-start">
                            <div className="mt-1 bg-blue-100 p-2 rounded-full mr-3 flex-shrink-0">
                              <Trophy className="w-5 h-5 text-taekwondo-blue" />
                            </div>
                            <div>
                              <p className="font-medium">Structured progression system with clear pathways for development</p>
                            </div>
                          </li>
                          <li className="flex items-start">
                            <div className="mt-1 bg-blue-100 p-2 rounded-full mr-3 flex-shrink-0">
                              <BarChart3 className="w-5 h-5 text-taekwondo-blue" />
                            </div>
                            <div>
                              <p className="font-medium">A positive, disciplined environment that fosters confidence & growth</p>
                            </div>
                          </li>
                          <li className="flex items-start">
                            <div className="mt-1 bg-blue-100 p-2 rounded-full mr-3 flex-shrink-0">
                              <Heart className="w-5 h-5 text-taekwondo-blue" />
                            </div>
                            <div>
                              <p className="font-medium">A strong Taekwondo community where students thrive together</p>
                            </div>
                          </li>
                          <li className="flex items-start">
                            <div className="mt-1 bg-blue-100 p-2 rounded-full mr-3 flex-shrink-0">
                              <Wifi className="w-5 h-5 text-taekwondo-blue" />
                            </div>
                            <div>
                              <p className="font-medium">Live-streamed classes so you never miss a session!</p>
                            </div>
                          </li>
                          <li className="flex items-start">
                            <div className="mt-1 bg-red-100 p-2 rounded-full mr-3 flex-shrink-0">
                              <ThumbsUp className="w-5 h-5 text-taekwondo-red" />
                            </div>
                            <div>
                              <p className="font-medium">Try us for FREE! – Enjoy a 2-week free trial and experience our classes firsthand</p>
                            </div>
                          </li>
                      
                        </ul>
                        <p className="text-gray-700 mt-6">
                          At OMAC-UK, we recognise that every student is unique. Our approach ensures that each individual gets the best out of themselves 
                          in a way that suits their learning style. Whether you're a beginner or a Black Belt, our Nottingham Taekwondo Academy 
                          is here to help you reach your full potential.
                        </p>
                        
                        <div className="mt-8 flex flex-col sm:flex-row items-center gap-4">
                          <div className="flex items-center text-gray-700 font-medium">
                            <MapPin className="w-5 h-5 text-taekwondo-red mr-2" />
                            Join OMAC-UK today and take the first step on your Taekwondo journey!
                          </div>
                          <Button className="bg-taekwondo-red hover:bg-taekwondo-red/90">
                            <Link to="/contact">
                            Start Your Free Trial
                            </Link>
                          </Button>

                        </div>
                      </div>
                      <div className="flex flex-col justify-center items-center gap-6">
                        <img 
                          src="/omac_logo.png"
                          alt="OMAC-UK Logo" 
                          className="w-[80%] h-auto max-w-md"
                        />
                        <img 
                          src="/images/about/dragons_medal.jpeg"
                          alt="OMAC-UK Students with medals" 
                          className="w-full h-auto rounded-xl shadow-md"
                        />
                      </div>

                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default About;
